import useStore from '../../../store'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wizard, WizardHeader, WizardWorkflow } from '../../ui/wizard'
import AuditUnitSteps from './AuditUnitSteps'
import { getConditionFromAuditUnit } from '../../../utilities/auditUtilities'
import requiredAuditPhotos from '../../../constants/requiredAuditPhotos'
import conditionReportPhotos from '../../../constants/conditionReportPhotos'

export default function AuditUnit() {
  const { fieldAuditId, auditUnitId } = useParams()
  const navigate = useNavigate()

  const {
    setTopNavHidden,
    fieldAuditUnits,
    updateAuditUnit,
    fetchAuditUnitFiles,
    deleteAuditUnitFile,
    auditUnitFiles,
    clearAuditUnitFiles,
    fetchFieldAuditUnits,
    fetchUnit,
    fieldAudit,
  } = useStore()

  const isConditionReport = String(fieldAudit?.id) === String(fieldAuditId) &&
    fieldAudit?.attributes?.['audit-type'] === 'Condition Report'
  const requiredPhotos = isConditionReport ? conditionReportPhotos : requiredAuditPhotos

  const [isEditing, setIsEditing] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [auditUnit, setAuditUnit] = useState(null)
  const [auditData, setAuditData] = useState({
    id: auditUnitId,
    field_audit_id: fieldAuditId,
    images: Object.fromEntries(requiredPhotos.map((photoTitle) => [photoTitle, null]))
  })

  useEffect(() => {
    const auditUnit = fieldAuditUnits.data.find((unit) =>
      unit.id === auditUnitId
    )
    setAuditUnit(auditUnit)
    fetchUnit(auditUnit.attributes['unit-id'])
    if (auditUnit.attributes['is-audited']) {
      // Only fetch audit unit files if unit has already been audited
      fetchAuditUnitFiles(auditUnitId)
    }
    if (String(auditUnitFiles?.[0]?.attributes['audit-unit-id']) !== String(auditUnitId)) {
      clearAuditUnitFiles()
    }
    setAuditData({
      ...auditData,
      condition: getConditionFromAuditUnit(auditUnit),
      can_refloor: auditUnit.attributes['can-refloor'] ? '1' : '0',
      notes: auditUnit.attributes['notes'] ?? '',
      current_location: auditUnit.attributes['current-location'] ?? '',
      location: auditUnit.attributes['location'] ?? '',
      ...(isConditionReport && {
        mileage: auditUnit.attributes['mileage'] ? String(auditUnit.attributes['mileage']) : '',
        key: auditUnit.attributes['key'] ? String(auditUnit.attributes['key']) : '',
        run_and_drive: auditUnit.attributes['run-and-drive'] ? String(auditUnit.attributes['run-and-drive']) : '',
      }),
    })
  }, [auditUnitId])

  useEffect(() => {
    if (auditUnitFiles?.length > 0 && String(auditUnitFiles[0].attributes['audit-unit-id']) === String(auditUnitId)) {
      let imagesToAdd = {}
      auditUnitFiles.forEach((file) => {
        imagesToAdd[file.attributes.name] = null
      })
      setAuditData((previous) => {
        return {
          ...previous,
          images: { ...previous.images, ...imagesToAdd }
        }
      })
    }
  }, [auditUnitFiles, auditUnitId])

  useEffect(() => {
    setTopNavHidden(true)

    return () => {
      setTopNavHidden(false)
    }
  }, [currentStep])

  const getSteps = () => AuditUnitSteps({
    auditUnit: auditUnit,
    auditData: auditData,
    auditUnitFiles: auditUnitFiles,
    setAuditData: setAuditData,
    setIsEditing: setIsEditing,
    isConditionReport: isConditionReport,
    requiredPhotos: requiredPhotos,
  })

  const goToFieldAudit = () => {
    navigate(`/field_audits/${fieldAuditId}`)
  }

  const handleBack = () => {
    if (currentStep <= 0 || (auditUnit?.attributes['is-audited'] && currentStep === getSteps().length - 1)) {
      goToFieldAudit()
    } else {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleNextStep = () => {
    if (isEditing) {
      setIsEditing(false)
      setCurrentStep(getSteps().length - 1)
    } else {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleComplete = async () => {
    const imagesToDelete = auditUnitFiles.filter((file) => auditData.images[file.attributes.name] !== null)
    imagesToDelete.forEach((file) => {
      deleteAuditUnitFile(auditUnitId, file.id)
    })

    const requestSent = await updateAuditUnit(
      fieldAuditId,
      auditUnitId,
      Object.fromEntries(Object.entries(auditData).filter(([key]) => key !== 'images')),
      auditData.images,
      () => fetchFieldAuditUnits(fieldAuditId)
    )

    if (requestSent) {
      goToFieldAudit()
    }

    return requestSent
  }

  return (
    <Wizard>
      <WizardHeader
        title={isConditionReport ? 'Condition Report' : 'Audit Unit'}
        progress={((currentStep + 1) / getSteps().length * 100)}
        showBackButton
        handleBack={handleBack}
      />
      <WizardWorkflow
        getSteps={getSteps}
        dependencies={[auditUnit, auditData, auditData.condition, auditData.images, auditUnitFiles]}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        handleNextStep={handleNextStep}
        onComplete={handleComplete}
      />
    </Wizard>
  )
}